/**
 * PublicCompanyPage — Phase 4
 * ────────────────────────────
 * Public, unauthenticated, SEO-friendly business profile.
 * URLs:
 *   /company/:slug
 *   /company/:slug/reviews   (deep link)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Building2, Calendar, Globe, Mail, MapPin, Phone, Star } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";
import Seo from "../../components/Seo";
import CompanyContactActions from "../../components/company/CompanyContactActions";
import PublicInquiryCTA from "../../components/company/PublicInquiryCTA";
import CompanySharePanel from "../../components/company/CompanySharePanel";
import CompanyPostsSection from "../../components/company/CompanyPostsSection";
import { findCity, ALL_CITIES } from "../../data/locations";

// iter299 — Localise `company.city` using the shared locations registry.
// Accepts either a canonical id ("Riyadh"), an English name, or an
// Arabic name — returns the matching {ar, en} row so the profile card
// renders "الرياض" in the Arabic UI and "Riyadh" in the English UI.
// Falls back to the raw value when the city isn't in the registry so
// no data is lost.
function _localizeCity(raw, lang) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  const needle = trimmed.toLowerCase();
  let match = findCity(trimmed);
  if (!match) {
    match = (ALL_CITIES || []).find(
      (c) => c.en?.toLowerCase() === needle || c.ar === trimmed,
    );
  }
  if (match) return lang === "ar" ? match.ar : match.en;
  return trimmed;
}

const CATEGORY_LABELS = {
  individual: "فرد",
  dealer: "معرض سيارات",
  official_agency: "وكالة رسمية",
  insurance_company: "شركة تأمين",
  finance_company: "شركة تمويل",
  rental_company: "شركة تأجير",
  transport_company: "شركة نقل",
  inspection_center: "مركز فحص",
  periodic_inspection: "فحص دوري",
  workshop: "ورشة",
  parts_store: "قطع غيار",
  tire_battery_shop: "إطارات وبطاريات",
  car_wash: "غسيل وتلميع",
  recovery_towing: "سطحة",
  salvage: "سكراب",
  auction_house: "دار مزادات",
  other: "أخرى",
};

export default function PublicCompanyPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState({ total: 0, items: [], caller_reviewed: false });
  const [products, setProducts] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewInput, setReviewInput] = useState({ rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get(`/platform/public/companies/${slug}`)
      .then(({ data }) => {
        setCompany(data);
        // fetch reviews + public products in parallel
        return Promise.all([
          apiClient.get(`/platform/reviews/company/${data.id}`).catch(() => ({ data: { total: 0, items: [], caller_reviewed: false } })),
          apiClient.get(`/platform/public/businesses/by-id/${data.id}/products`).catch(() => ({ data: { items: [] } })),
        ]);
      })
      .then(([revRes, prodRes]) => {
        setReviews(revRes?.data || { total: 0, items: [], caller_reviewed: false });
        setProducts(prodRes?.data?.items || []);
      })
      .catch(() => setCompany(null))
      .finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  // iter331 — <title> is now owned by <Seo/> below (SEO Phase 1.5). The
  // previous `useEffect(() => { document.title = ... })` created a second
  // <title> element in the DOM that competed with Helmet, so we removed
  // it. Helmet's <title> is the single source of truth.

  const submitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/platform/reviews", { business_id: company.id, rating: reviewInput.rating, text: reviewInput.text });
      toast.success(lang === "ar" ? "تم إرسال تقييمك" : "Review submitted");
      setShowReviewForm(false); setReviewInput({ rating: 5, text: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-[70vh] flex items-center justify-center text-[#525252]">…</div>;
  if (!company) {
    return (
      <div className="max-w-2xl mx-auto my-24 p-8 rounded-2xl bg-white border border-[#0a0a0a]/[0.06] text-center">
        <div className="text-lg font-bold mb-2">{lang === "ar" ? "الشركة غير موجودة" : "Company not found"}</div>
        <Link to="/companies" className="text-[#8a6f2e] underline text-sm">
          {lang === "ar" ? "تصفح دليل الشركات" : "Browse companies"}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#fafaf7]" data-testid="public-company-page">
      {/* SEO Phase 1 — dynamic company metadata. Uses PUBLIC company
          fields only (name + city). No contact / internal data leaks. */}
      <Seo
        title={(() => {
          const name = lang === "ar" ? (company.name_ar || company.name_en) : (company.name_en || company.name_ar);
          const city = lang === "ar" ? (company.city_ar || company.city_en) : (company.city_en || company.city_ar);
          const cityPart = city ? ` | ${city}` : "";
          return lang === "ar" ? `${name}${cityPart} | كارتال` : `${name}${cityPart} | CARTAL`;
        })()}
        description={(() => {
          const name = lang === "ar" ? (company.name_ar || company.name_en) : (company.name_en || company.name_ar);
          const city = lang === "ar" ? (company.city_ar || company.city_en) : (company.city_en || company.city_ar);
          const cityPart = city ? (lang === "ar" ? ` في ${city}` : ` in ${city}`) : "";
          return lang === "ar"
            ? `صفحة ${name}${cityPart} على منصة كارتال — إعلانات حالية، تقييمات، وبيانات تواصل.`
            : `${name}${cityPart} on CARTAL — current listings, reviews and contact details.`;
        })()}
        jsonLd={(() => {
          // iter332 — AutoDealer schema uses ONLY public business fields.
          // No owner personal data (phone/email/NIN) is included. Address
          // locality is included when city is public; otherwise omitted.
          const nameAr = company.name_ar || company.name_en || "CARTAL business";
          const nameEn = company.name_en || company.name_ar || "CARTAL business";
          const slug = company.slug || "";
          const canonicalUrl = `https://cartal.sa/company/${encodeURIComponent(slug)}`;
          const city = company.city_ar || company.city_en || null;
          const dealer = {
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "url": canonicalUrl,
            "name": lang === "ar" ? nameAr : nameEn,
            "alternateName": lang === "ar" ? nameEn : nameAr,
            ...(company.logo_url ? { "image": company.logo_url } : {}),
            ...(city ? {
              "address": {
                "@type": "PostalAddress",
                "addressLocality": city,
                "addressCountry": "SA",
              },
              "areaServed": city,
            } : { "address": { "@type": "PostalAddress", "addressCountry": "SA" } }),
          };
          const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": lang === "ar" ? "الرئيسية" : "Home", "item": "https://cartal.sa/" },
              { "@type": "ListItem", "position": 2, "name": lang === "ar" ? "الشركات" : "Companies", "item": "https://cartal.sa/companies" },
              { "@type": "ListItem", "position": 3, "name": dealer.name, "item": canonicalUrl },
            ],
          };
          return [dealer, breadcrumb];
        })()}
      />
      {/* Cover */}
      <div className="relative h-40 sm:h-56 lg:h-64 overflow-hidden" style={{
        background: company.cover_url ? `url(${company.cover_url}) center/cover` : "linear-gradient(135deg,#0a0a0a 0%,#141110 50%,#1f1614 100%)",
      }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,transparent 40%,rgba(0,0,0,0.4))" }} />
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10 -mt-16 sm:-mt-20 relative">
        {/* Header card */}
        <div className="rounded-2xl bg-white shadow-lg border border-[#0a0a0a]/[0.06] p-5 sm:p-6">
          <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl overflow-hidden border-4 border-white shadow-xl shrink-0 -mt-14 sm:-mt-16"
                 style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
              {company.logo_url ? (
                <img src={company.logo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-[#0a0a0a]">
                  {(company.name_ar || "?").charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-2xl sm:text-3xl font-display font-bold text-[#0a0a0a]">{company.name_ar}</h1>
                {company.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: "rgba(201,165,92,0.18)", color: "#8a6f2e", border: "1px solid rgba(201,165,92,0.5)" }} data-testid="verified-badge">
                    <BadgeCheck className="w-3.5 h-3.5" /> {lang === "ar" ? "موثّق" : "Verified"}
                  </span>
                )}
              </div>
              <div className="text-sm text-[#525252]">
                {company.name_en && <span className="me-2 font-mono opacity-75">{company.name_en}</span>}
                <span className="inline-flex items-center gap-1 me-3">
                  <Building2 className="w-3.5 h-3.5" /> {CATEGORY_LABELS[company.category] || company.category}
                </span>
                {(() => { const cn = _localizeCity(company.city, lang); return cn && (<span className="inline-flex items-center gap-1 me-3" data-testid="company-city"><MapPin className="w-3.5 h-3.5" /> {cn}</span>); })()}
                {company.founded_year && <span className="inline-flex items-center gap-1" data-testid="stat-founded"><Calendar className="w-3.5 h-3.5" /> {lang === "ar" ? `منذ ${company.founded_year}م` : `Since ${company.founded_year}`}</span>}
              </div>
            </div>
            {/* Rating pill */}
            <div className="rounded-2xl px-4 py-3 shrink-0" style={{ background: "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
              <div className="flex items-center gap-1 mb-0.5">
                <Star className="w-4 h-4" fill="#e8c873" stroke="#e8c873" />
                <span className="text-2xl font-bold text-[#f7eecd]">{company.reviews.average ?? "—"}</span>
              </div>
              <div className="text-[10px] text-[#f7eecd]/60 uppercase tracking-wider">{company.reviews.total} {lang === "ar" ? "تقييم" : "reviews"}</div>
            </div>
          </div>

          {/* Stats strip removed — iter298: service-provider profiles
              (workshops/care centres/transport) don't publish
              marketplace listing counters. Card flows straight from the
              header into the CTA buttons. */}

          {/* Phase 6 — Message & Book buttons (additive) */}
          <div className="mt-5 pt-4 border-t border-[#0a0a0a]/[0.06] space-y-3">
            <CompanyContactActions business={{ id: company.id, name_ar: company.name_ar, name_en: company.name_en }} />
            {/* Phase 2D — Category-adaptive Public Inquiry CTA */}
            <PublicInquiryCTA business={company} />
          </div>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {(company.bio_ar || company.bio_en) && (
              <Card labelAr="عن الشركة" labelEn="About">
                <div className="text-sm leading-relaxed text-[#0a0a0a]">
                  {lang === "ar" ? company.bio_ar : company.bio_en || company.bio_ar}
                </div>
              </Card>
            )}

            {/* iter332 — Activity posts (visual marketing surface, not a
                vehicle listing). Renders above recent listings so the
                company's brand story leads the profile. */}
            <CompanyPostsSection company={company} />

            {company.recent_listings?.length > 0 && (
              <Card labelAr="أحدث الإعلانات" labelEn="Recent listings" testid="recent-listings">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {company.recent_listings.map((v) => (
                    <Link key={v.id} to={`/vehicle/${v.id}`} className="group block">
                      <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#f0eee7] mb-2">
                        {v.images?.[0] && <img src={v.images[0]} alt={v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                      </div>
                      <div className="text-xs text-[#8a6f2e] font-mono">{v.ad_number}</div>
                      <div className="text-sm font-semibold text-[#0a0a0a] truncate">
                        {v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}
                      </div>
                      <div className="text-[12px] text-[#525252]">{v.price_sar ? `${Number(v.price_sar).toLocaleString()} SAR` : "—"}</div>
                    </Link>
                  ))}
                </div>
              </Card>
            )}

            {/* Phase 2B — Products & Services on the public profile */}
            {products.length > 0 && (
              <Card labelAr="المنتجات والخدمات" labelEn="Products & Services" testid="public-products-section">
                <div className="grid sm:grid-cols-2 gap-3">
                  {products.map((p) => {
                    const kindLbl = {
                      finance_product:   { ar: "تمويل", en: "Finance" },
                      bnpl_plan:         { ar: "تقسيط", en: "BNPL" },
                      leasing_product:   { ar: "تأجير تمويلي", en: "Leasing" },
                      insurance_product: { ar: "تأمين", en: "Insurance" },
                      service:           { ar: "خدمة", en: "Service" },
                      part:              { ar: "قطعة", en: "Part" },
                      payment_solution:  { ar: "دفع", en: "Payment" },
                      offer:             { ar: "عرض", en: "Offer" },
                      other:             { ar: "أخرى", en: "Other" },
                    }[p.kind] || { ar: p.kind, en: p.kind };
                    return (
                      <div key={p.id} className="rounded-xl border border-[#0a0a0a]/[0.06] p-3 bg-[#fafaf7]" data-testid={`public-product-${p.id}`}>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <div className="text-[13px] font-bold text-[#0a0a0a] truncate">{lang === "ar" ? p.name_ar : (p.name_en || p.name_ar)}</div>
                          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white text-[#8a6f2e] border border-[#c9a55c]/25">
                            {lang === "ar" ? kindLbl.ar : kindLbl.en}
                          </span>
                        </div>
                        {p.description_ar && (
                          <div className="text-[11px] text-[#525252] line-clamp-2">
                            {lang === "ar" ? p.description_ar : (p.description_en || p.description_ar)}
                          </div>
                        )}
                        {p.price_sar != null && (
                          <div className="text-[12px] text-[#0a0a0a] mt-1">
                            <span className="font-bold">{Number(p.price_sar).toLocaleString()}</span> <span className="text-[10px] text-[#8a8a8a]">{lang === "ar" ? "ر.س" : "SAR"}</span>
                          </div>
                        )}
                        {p.spec && Object.keys(p.spec).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {Object.entries(p.spec).slice(0, 3).map(([k, v]) => (
                              <span key={k} className="text-[9px] bg-white rounded px-1 py-0.5 border border-[#0a0a0a]/[0.05]">
                                <span className="text-[#8a6f2e] font-bold">{k}:</span> {String(v).slice(0, 32)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}



            <Card labelAr="التقييمات" labelEn="Reviews" testid="reviews-section"
                  action={user && !reviews.caller_reviewed && company.id !== user.id ? (
                    <button type="button" onClick={() => setShowReviewForm(true)}
                            className="text-[12px] font-bold px-3 py-1.5 rounded-full"
                            style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                            data-testid="btn-write-review">
                      {lang === "ar" ? "أضف تقييم" : "Write review"}
                    </button>
                  ) : null}>
              {reviews.items.length === 0 ? (
                <div className="text-sm text-[#8a8a8a]">{lang === "ar" ? "لا توجد تقييمات بعد" : "No reviews yet"}</div>
              ) : (
                <div className="divide-y divide-[#0a0a0a]/[0.06]">
                  {reviews.items.map((r) => (
                    <div key={r.id} className="py-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-semibold">{r.reviewer_name || (lang === "ar" ? "عميل" : "Customer")}</div>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} className="w-3 h-3" fill={r.rating >= i ? "#c9a55c" : "none"} stroke="#c9a55c" strokeWidth={1.5} />
                          ))}
                        </div>
                      </div>
                      {r.text && <div className="text-sm text-[#525252]">{r.text}</div>}
                      <div className="text-[10px] text-[#8a8a8a] mt-1">{r.created_at?.slice(0, 10)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Contact / sidebar */}
          <div className="space-y-4">
            <Card labelAr="التواصل" labelEn="Contact">
              <div className="space-y-2 text-sm">
                {company.phone && <ContactRow icon={Phone} text={company.phone} ltr href={`tel:${String(company.phone).replace(/\s+/g, "")}`} testid="contact-phone" />}
                {company.email && <ContactRow icon={Mail} text={company.email} ltr href={`mailto:${String(company.email).trim()}`} testid="contact-email" />}
                {company.website && <ContactRow icon={Globe} text={company.website} ltr href={company.website} />}
                {company.address_ar && <ContactRow icon={MapPin} text={company.address_ar} />}
              </div>
            </Card>

            {/* iter290 — Share / Embed / QR / Map — free marketing kit
                for verified partners. Renders for owners AND visitors so
                anyone visiting the profile can help distribute it. */}
            <CompanySharePanel company={company} />

            {company.branches?.length > 0 && (
              <Card labelAr={`الفروع (${company.branches.length})`} labelEn={`Branches (${company.branches.length})`}>
                <div className="space-y-2">
                  {company.branches.map((b) => (
                    <div key={b.id} className="text-sm">
                      <div className="font-semibold">{b.name_ar}{b.is_headquarters && (<span className="ms-1 text-[10px] text-[#8a6f2e]">· {lang === "ar" ? "المقر" : "HQ"}</span>)}</div>
                      {b.city && <div className="text-[12px] text-[#525252]">{b.city}</div>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>

        <div className="h-16" />
      </div>

      {/* Review modal */}
      {showReviewForm && (
        <>
          <button type="button" aria-label="close" onClick={() => setShowReviewForm(false)} className="fixed inset-0 z-40 bg-black/40" />
          <div className="fixed inset-x-4 top-24 sm:inset-x-auto sm:right-1/2 sm:translate-x-1/2 sm:max-w-md z-50 rounded-2xl bg-white shadow-2xl p-5">
            <form onSubmit={submitReview} className="space-y-3">
              <h3 className="text-lg font-bold">{lang === "ar" ? "تقييم الشركة" : "Rate this company"}</h3>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setReviewInput({ ...reviewInput, rating: i })} data-testid={`star-${i}`}>
                    <Star className="w-7 h-7" fill={reviewInput.rating >= i ? "#c9a55c" : "none"} stroke="#c9a55c" strokeWidth={1.5} />
                  </button>
                ))}
              </div>
              <textarea rows={3} value={reviewInput.text} onChange={(e) => setReviewInput({ ...reviewInput, text: e.target.value })}
                        placeholder={lang === "ar" ? "شاركنا تجربتك (اختياري)" : "Share your experience (optional)"}
                        className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowReviewForm(false)} className="px-4 py-2 rounded-full text-sm text-[#525252]">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                <button type="submit" disabled={submitting} data-testid="btn-submit-review"
                        className="px-4 py-2 rounded-full text-sm font-bold disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}>
                  {submitting ? "…" : (lang === "ar" ? "إرسال" : "Submit")}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ labelAr, labelEn, value, testid }) {
  const { lang } = useI18n();
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-2xl font-bold font-display text-[#0a0a0a]">{(value || 0).toLocaleString()}</div>
      <div className="text-[11px] text-[#8a8a8a]">{lang === "ar" ? labelAr : labelEn}</div>
    </div>
  );
}

function Card({ labelAr, labelEn, children, testid, action }) {
  const { lang } = useI18n();
  return (
    <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">{lang === "ar" ? labelAr : labelEn}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, text, ltr, href, testid }) {
  const inner = (
    <div className="inline-flex items-center gap-2 text-[#0a0a0a]" dir={ltr ? "ltr" : undefined}>
      <Icon className="w-4 h-4 text-[#8a6f2e]" />
      <span className="truncate">{text}</span>
    </div>
  );
  if (!href) return <div>{inner}</div>;
  // iter300 — tel: / mailto: open the native dialer or mail composer.
  // Only http(s) links get target="_blank"; native protocol handlers
  // must open in the same context so the OS intercepts them properly.
  const isExternal = /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="hover:underline block"
      data-testid={testid}
    >
      {inner}
    </a>
  );
}
